import type { KetteEinbauArt } from '@hooks/states/echainConfigSlice';
import type { ExchangeConfigurationV1 } from '@igus/echain-shared-exchange';
import type { InteriorSeparationConfigurationFiles } from '@hooks/states/echainPartsListSlice';

export enum ConfigurationType {
  CFC = 'CFC',
  ECC = 'ECC',
}

/**
 * The response body of an add-to-shopping-cart api request.
 */
export type AddItemsToShoppingCartResponse = {
  cartArticleCount: number;
};

/**
 * The response body of an add-configuration-to-shopping-cart or an update-configuration-in-shopping-cart api request.
 */
export type SendConfigurationToShoppingCartResponse = {
  configurationType: string;
  configurationId: string;
  currency: string;
  decimalPlaces: number;
  unitPrice: number;
  copperPrice: number;
  cuttingCosts: number;
  cartArticleCount: number;
};

/**
 * The request body of an /shopping-cart/position?cartPositionId
 */
export interface ShoppingCartPositionResponse {
  cartPositionId: string;
  configurationType: string;
  configurationId: string;
  quantity: number;
  articles: ShoppingCartItem[];
  data?: unknown;
}

/**
 * The echain data to be persisted at the shopping cart
 */
export interface EchainShoppingCartData {
  shopSettings: {
    ketteEinbauart?: KetteEinbauArt;
  };
  iaConfiguratorData?: ExchangeConfigurationV1;
  files?: InteriorSeparationConfigurationFiles;
}

/**
 * Guard to ensure correct shopping cart data type
 */
export const isEchainShoppingCartData = (data: unknown): data is EchainShoppingCartData =>
  (data as EchainShoppingCartData).shopSettings !== undefined;

/**
 * The request body of an add-to-shopping-cart api request.
 */
export interface AddItemsToShoppingCartRequest {
  articles: ShoppingCartItem[];
}

/**
 * The request body of an add-configuration-to-shopping-cart or update-configuration-in-shopping-cart api request.
 */
export interface SendConfigurationToShoppingCartRequest<DataT> {
  configurationType: ConfigurationType;
  configurationId?: string;
  quantity: number;
  data?: DataT;
  articles: ShoppingCartItem[];
  files?: { name: string; content: string }[];
  cartPositionId?: string;
}

/**
 * A single shopping-cart item.
 */
export interface ShoppingCartItem {
  articleNumber: string;
  quantity: number;
  length?: number;
  unit?: string;
}

export interface ShoppingCardConfigurationRequest {
  articles: ShoppingCartItem[];
  configurationId?: string;
}

/**
 * ShoppingCart ConfigurationId Response
 */
export interface ShoppingCartConfigurationIdResponse {
  configurationType: string;
  configurationId: string;
}

/**
 * A request object to fetch details for articles
 */
export interface ArticleDetailRequest {
  configurationType?: string;
  priceCountry?: string;
  articleNumbers: string[];
  length?: number;
  unit?: string;
  textMode?: boolean;
}

export interface ArticleDetails {
  articleNumber: string;
  priceScales: { quantity: number; prices: number[] }[];
  priceScaleTypes: string[];
  messages: string[];
  dt: number;
  dtText: string;
}

export interface ArticleConfigTotalPriceAndDeliveryRequestArticle {
  articleNumber: string;
  quantity: number;
  length?: number;
  unit?: string;
}

export interface ArticleConfigTotalPriceAndDeliveryTimesRequest {
  configurationType?: string;
  quantity?: number;
  articles: ArticleConfigTotalPriceAndDeliveryRequestArticle[];
}

export interface ArticleConfigTotalPriceAndDeliveryTimesResponse {
  currency: string;
  decimalPlaces: number;
  total: number;
  copperPrice: number;
  cuttingCosts: number;
  dt: number;
  dtText: string;
}

export interface ArticlePriceDetails {
  articleNumber: string;
  unitPrice: number;
  orderingType: number;
}

export interface ArticlePriceResponse {
  articles: ArticlePriceDetails[];
  currency: string;
  decimalPlaces: number;
}

export interface ArticleDetailResponse<T extends 'text' | 'number'> {
  articles: T extends 'text' ? TextArticle[] : NumberArticle[];
  currency: T extends 'number' ? string : never; // Only for "number" mode
  decimalPlaces: T extends 'number' ? number : never; // Only for "number" mode
}

interface BaseArticle {
  articleNumber: string;
  messages: string[];
  dt: number;
  dtText: string;
  orderingType?: number;
  length?: number;
  unit?: string;
}

interface TextArticle extends BaseArticle {
  priceScales: TextPriceScale[];
}

interface NumberArticle extends BaseArticle {
  priceScales: NumberPriceScale[];
  priceScaleTypes: string[];
}

interface TextPriceScale {
  quantityText: string;
  priceText: string;
}

interface NumberPriceScale {
  quantity: number;
  prices: number[];
}
